import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Mail, Pencil, Plus, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Skeleton } from "@fleet/ui";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@mealplan/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmailTemplateEditor } from "./email-template-editor";
import { QueryErrorState } from "@/components/ui/query-error-state";

type EmailTemplate = components["schemas"]["EmailTemplate"];

interface Props {
  /** Editor in create mode (`?new=1`). */
  creating?: boolean;
  /** Editor for an existing template (`?edit=<id>`). */
  editingId?: string;
  onNew?: () => void;
  onEdit?: (id: string) => void;
  onClose?: () => void;
}

/**
 * CRUD for email templates (campaign #11). Lists the templates (system +
 * custom), and allows creating/editing/deleting. System ones cannot be deleted.
 *
 * Editor state (create/edit) addressable by URL (`?new=1` / `?edit=<id>`,
 * campaign #15): the route is the source of truth for sharing/reopening.
 */
export function EmailTemplateManager(props: Props = {}) {
  const { t } = useI18n();

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  // The template being edited is resolved by id from the list; while it has not
  // loaded, we keep the editor mounted but without data (it hydrates on arrival).
  const editing = createMemo<EmailTemplate | null>(
    () => (templatesQuery.data ?? []).find((tpl) => tpl.id === props.editingId) ?? null,
  );
  const creating = (): boolean => Boolean(props.creating);
  // The editor is visible if creation is requested, or if there is an id to edit (even if
  // the template has not arrived yet, to avoid flickering during loading).
  const editorVisible = (): boolean => creating() || Boolean(props.editingId);

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    props.onClose?.();
    await templatesQuery.refetch();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        <Button class="gap-2" onClick={() => props.onNew?.()} data-testid="email-new-template">
          <Plus class="h-4 w-4" />
          {t(msg`New Template`)}
        </Button>
      </div>

      {/* `keyed` por id (o "new") remonta el editor al cambiar de plantilla, ya
          que inicializa sus señales desde props en el constructor. */}
      <Show when={editorVisible() && (props.editingId ?? "new")} keyed>
        <div data-testid="email-template-editor">
          <EmailTemplateEditor
            template={editing()}
            onSaved={onSaved}
            onCancel={() => props.onClose?.()}
          />
        </div>
      </Show>

      {/* P23 / CC-60 · I3 — el estado de ERROR, envuelto y NO encadenado. Encadenar `&& !isError` en el guard de abajo no vale: en error `isLoading` es false, el guard pinta su fallback y quedan la caja de error Y el esqueleto a la vez, para siempre. */}
      <Show when={templatesQuery.isError}>
        <QueryErrorState
          testId="email-templates-error"
          compact
          onRetry={() => void templatesQuery.refetch()}
        />
      </Show>
      <Show when={!templatesQuery.isError}>
        <Show
          when={!templatesQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For each={templatesQuery.data ?? []}>
              {(tpl) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{tpl.name}</span>
                      <Badge variant="secondary">{tpl.category}</Badge>
                      <Show when={tpl.isSystem}>
                        <Badge>{t(msg`System`)}</Badge>
                      </Show>
                      <Show when={!tpl.isActive}>
                        <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                      </Show>
                    </div>
                    <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {(() => {
                        const { key } = tpl;
                        const version = tpl.currentVersion;
                        return t(msg`Key: ${key} · v${version}`);
                      })()}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      class="gap-1"
                      onClick={() => props.onEdit?.(tpl.id)}
                    >
                      <Pencil class="h-4 w-4" />
                      {t(msg`Edit`)}
                    </Button>
                    <Show when={!tpl.isSystem}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => handleDelete(tpl)}
                      >
                        <Trash2 class="h-4 w-4" />
                        {t(msg`Delete`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
